import { pricing } from './pricing'
import { brandWordmark } from './brandAssets'
import { OPERATOR_NAME, SUPPORT_EMAIL } from './links'

/**
 * Route-level search and social metadata. One entry per real route. Titles and
 * descriptions are factual and drawn from the same facts the pages show.
 * Never use em dashes here (docs/COPY_RULES.md).
 */

/** Production canonical host. The apex domain 301-redirects here at the host. */
export const SITE_URL = 'https://www.lightninglearn.io'

/** Only this host may be indexed. Previews, drafts and any other host stay noindex. */
export const PRODUCTION_HOSTNAME = 'www.lightninglearn.io'

export const SITE_NAME = 'Lightning Learn'

/** Existing approved artwork reused as the social preview image. */
export const SOCIAL_IMAGE = {
  /** Always absolute HTTPS, even if the asset path is exported as relative. */
  src: new URL(brandWordmark.src, SITE_URL).href,
  /** Matches the actual PNG wordmark file. */
  type: 'image/png',
  width: brandWordmark.width,
  height: brandWordmark.height,
  alt: 'Lightning Learn wordmark',
}

export interface RouteSeo {
  path: string
  title: string
  description: string
  /** Visible breadcrumb label. Omitted for the homepage. */
  breadcrumb?: string
  /** Index on the production host. False adds noindex,follow. */
  indexable: boolean
}

export const routeSeo: RouteSeo[] = [
  {
    path: '/',
    title: 'Lightning Learn: Illustrated learning adventures for curious kids',
    description: `A learning app for curious kids ${pricing.ageLabel}: ${pricing.lessonCount} bite-sized illustrated adventures across ${pricing.worldCount} worlds that kids can read or listen to. Coming to iPhone.`,
    indexable: true,
  },
  {
    path: '/adventure',
    title: 'Try a free illustrated adventure | Lightning Learn',
    description:
      'Step through a free sample adventure, “Why Do Octopuses Have Three Hearts?”, card by card in your browser. No account, payment or email needed.',
    breadcrumb: 'Free adventure',
    indexable: true,
  },
  {
    path: '/worlds',
    title: `${pricing.worldCount} worlds of illustrated adventures | Lightning Learn`,
    description: `Browse all ${pricing.worldCount} Lightning Learn worlds, from Amazing Animals to Space & Beyond. Ten illustrated adventures in each, ${pricing.lessonCount} in all.`,
    breadcrumb: 'Worlds',
    indexable: true,
  },
  {
    path: '/parents',
    title: 'For parents | Lightning Learn',
    description: `How Lightning Learn works for families: no ads, no chat, separate profiles for up to ${pricing.maxProfiles} children, saved progress and parent-protected settings.`,
    breadcrumb: 'For parents',
    indexable: true,
  },
  {
    path: '/pricing',
    title: `Pricing: ${pricing.monthlyPrice} a month or ${pricing.annualPrice} a year | Lightning Learn`,
    description: `Lightning Learn is ${pricing.monthlyPrice} billed monthly or ${pricing.annualPrice} billed yearly, paid upfront. No free trial. Subscriptions are managed in the iPhone app.`,
    breadcrumb: 'Pricing',
    indexable: true,
  },
  {
    path: '/support',
    title: 'Support | Lightning Learn',
    description: `Get help with Lightning Learn. Email ${SUPPORT_EMAIL} or read answers to common questions about ages, billing and child profiles.`,
    breadcrumb: 'Support',
    indexable: true,
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | Lightning Learn',
    description:
      'How Black Comet Labs, LLC handles information on lightninglearn.io, in the Lightning Learn app during private testing, and when you contact support. Effective November 1, 2026.',
    breadcrumb: 'Privacy Policy',
    indexable: false,
  },
  {
    path: '/terms',
    title: 'Terms and Conditions | Lightning Learn',
    description:
      'Terms for using lightninglearn.io and the Lightning Learn service from Black Comet Labs, LLC, including subscriptions, child profiles and deletion. Effective November 1, 2026.',
    breadcrumb: 'Terms and Conditions',
    indexable: false,
  },
]

export const notFoundSeo: RouteSeo = {
  path: '',
  title: 'Page not found | Lightning Learn',
  description: 'This page does not exist. Head back to Lightning Learn to explore worlds or try a free adventure.',
  breadcrumb: 'Page not found',
  indexable: false,
}

/** Strip a trailing slash (except root) so "/worlds/" and "/worlds" match. */
export function normalizePath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : '/'
}

/** The route entry for a path, or null for an unknown path. */
export function findRouteSeo(pathname: string): RouteSeo | null {
  const path = normalizePath(pathname)
  return routeSeo.find((route) => route.path === path) ?? null
}

/** Canonical URL: production host, normalized path, no query or hash. */
export function canonicalUrl(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`
}

/** Honest structured data for a page. No ratings, addresses, dates or app listings. */
export function buildStructuredData(route: RouteSeo) {
  const orgId = `${SITE_URL}/#organization`
  const siteId = `${SITE_URL}/#website`
  const url = canonicalUrl(route.path)

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      '@id': orgId,
      name: OPERATOR_NAME,
      url: `${SITE_URL}/`,
      email: SUPPORT_EMAIL,
      logo: SOCIAL_IMAGE.src,
      brand: { '@type': 'Brand', name: SITE_NAME },
    },
    {
      '@type': 'WebSite',
      '@id': siteId,
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      publisher: { '@id': orgId },
      inLanguage: 'en-US',
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      name: route.title,
      description: route.description,
      url,
      isPartOf: { '@id': siteId },
      inLanguage: 'en-US',
    },
  ]

  if (route.breadcrumb && route.path !== '/') {
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: route.breadcrumb, item: url },
      ],
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}
